import { memo, useCallback, useState } from 'react'

import { PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { useContactData } from 'clickNgo/features/contact/contactDeactivation/contexts'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { NotificationController } from 'core-components/library/popup'
import Logger from 'core/utils/Logger'

import { ContactRelinkingError, ContactRelinkingStep } from '../../../configs'
import { useReassignment } from '../../../contexts/ReassignmentContext'
import { ContactRelinkingWelcomeStepProps } from '../../../models'
import { contactRelinkingModalTranslations } from '../../../translation'
import { ContactRelinkingFooter } from '../../contactRelinkingFooter'
import { ContactRelinkingHeader } from '../../contactRelinkingHeader'

import { determineFlagsToReassign } from './helpers/determineFlagsToReassign'
import { determineRolesToReassign } from './helpers/determineRolesToReassign'
import { loadPartnerData } from './helpers/loadPartnerData'

import styles from './WelcomeStep.module.scss'

export const WelcomeStep = memo<ContactRelinkingWelcomeStepProps>(
  ({ onProceed, onCancel, relinkingNewPartnerId }) => {
    const { contact, contactFlags } = useContactData()
    const {
      setAvailableContacts,
      setRolesToReassign,
      setFlagsToReassign,
      setUniqueRolesToReassign,
      setTargetPartnerContactsRoles,
      setIsAskingHowRemoveUniqueRoles,
    } = useReassignment()

    const [loaderState, setLoaderState] = useState({ isLoading: false, message: '' })

    const handleProceedClick = useCallback(async () => {
      try {
        setLoaderState({ isLoading: true, message: 'Checking target partner and validating...' })

        const { availableContacts, targetPartnerContacts, targetPartner } = await loadPartnerData({
          contact,

          relinkingNewPartnerId,
        })

        const {
          rolesToReassign,
          uniqueRolesToReassign,
          targetPartnerContactsRoles,
          isAskingHowRemoveUniqueRoles,
          isNeedReassignRoles,
          isNeedReassignUniqueRoles,
        } = determineRolesToReassign({
          contact,
          targetPartner,
          availableContacts,
          targetPartnerContacts,
        })

        const { flagsToReassign, isNeedReassignFlag } = await determineFlagsToReassign({
          contact,
          availableContacts,
          contactFlags,
        })

        setAvailableContacts(availableContacts)

        setRolesToReassign(rolesToReassign)
        setUniqueRolesToReassign(uniqueRolesToReassign)
        setFlagsToReassign(flagsToReassign)

        setIsAskingHowRemoveUniqueRoles(isAskingHowRemoveUniqueRoles)
        setTargetPartnerContactsRoles(targetPartnerContactsRoles)

        if (
          availableContacts.length === 0 &&
          ![PartnerHierarchyType.BRANCH, PartnerHierarchyType.ONLINE].includes(
            contact.hierarchyType
          )
        ) {
          Logger.info('No available contacts found for reassignment.')
          onProceed(ContactRelinkingStep.Backup)
        } else if (isNeedReassignUniqueRoles) {
          Logger.info('Roles to reassign found:', uniqueRolesToReassign)
          onProceed(ContactRelinkingStep.ReassignUniqueRoles)
        } else if (isNeedReassignRoles) {
          Logger.info('Roles to reassign found:', rolesToReassign)
          onProceed(ContactRelinkingStep.ReassignRoles)
        } else if (isNeedReassignFlag) {
          Logger.info('Flags to reassign found:', flagsToReassign)
          onProceed(ContactRelinkingStep.ReassignFlags)
        } else {
          Logger.info('No roles or flags to reassign found.')
          onProceed(ContactRelinkingStep.Completion)
        }
      } catch (error) {
        Logger.error(ContactRelinkingError.FetchFail)
        NotificationController.showError({
          message: `${ContactRelinkingError.FetchFail}: ${error}`,
        })
      } finally {
        setLoaderState({ isLoading: false, message: '' })
      }
    }, [
      contact,
      relinkingNewPartnerId,
      contactFlags,
      setAvailableContacts,
      setRolesToReassign,
      setUniqueRolesToReassign,
      setFlagsToReassign,
      setIsAskingHowRemoveUniqueRoles,
      setTargetPartnerContactsRoles,
      onProceed,
    ])

    return (
      <div className={styles.container}>
        <LoaderOverlay isVisible={loaderState.isLoading} message={loaderState.message} />
        <ContactRelinkingHeader
          title={contactRelinkingModalTranslations.welcomeStep.title}
          description={contactRelinkingModalTranslations.welcomeStep.description}
          descriptionParams={[{ key: '{email}', value: contact.email }]}
        />
        <ContactRelinkingFooter onCancel={onCancel} onProceed={handleProceedClick} />
      </div>
    )
  }
)

WelcomeStep.displayName = 'WelcomeStep'
